import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import { PageHeader } from '../components/Layout';
import { ErrorState, Loading, TextInput, useToast } from '../components/ui';

interface ProviderRow {
  provider: 'RAZORPAY' | 'PAYU' | 'CASHFREE' | 'STRIPE';
  keyId: string;
  hasSecret: boolean;
  isLive: boolean;
  isEnabled: boolean;
  updatedAt: string | null;
}

const LABELS: Record<ProviderRow['provider'], { name: string; keyLabel: string; note: string }> = {
  RAZORPAY: {
    name: 'Razorpay',
    keyLabel: 'Key ID',
    note: 'Dashboard → Settings → API Keys. Test keys start rzp_test_, live ones rzp_live_.',
  },
  PAYU: { name: 'PayU', keyLabel: 'Merchant key', note: 'From your PayU merchant dashboard.' },
  CASHFREE: {
    name: 'Cashfree',
    keyLabel: 'App ID',
    note: 'Cashfree dashboard → Developers → API Keys.',
  },
  STRIPE: {
    name: 'Stripe',
    keyLabel: 'Publishable key',
    note: 'Stripe dashboard → Developers → API keys.',
  },
};

/**
 * A hotel's own gateway credentials.
 *
 * The secret is written once and never read back — the screen is told only
 * that one is stored, because anything this page can display, a compromised
 * browser session can steal.
 */
export function PaymentSettingsPage() {
  const queryClient = useQueryClient();
  const { push, fail } = useToast();
  const [drafts, setDrafts] = useState<Record<string, { keyId?: string; keySecret?: string }>>({});

  const providers = useQuery({
    queryKey: ['payment-settings'],
    queryFn: () => api.get<ProviderRow[]>('/api/payment-settings'),
  });

  const save = useMutation({
    mutationFn: (input: {
      provider: string;
      keyId?: string;
      keySecret?: string;
      isLive?: boolean;
      isEnabled?: boolean;
    }) => api.put(`/api/payment-settings/${input.provider}`, input),
    onSuccess: (_r, input) => {
      push(`${LABELS[input.provider as ProviderRow['provider']].name} saved`, 'ok');
      setDrafts((d) => ({ ...d, [input.provider]: {} }));
      void queryClient.invalidateQueries({ queryKey: ['payment-settings'] });
    },
    onError: fail,
  });

  if (providers.isLoading) return <Loading />;
  if (providers.error)
    return <ErrorState error={providers.error} onRetry={() => void providers.refetch()} />;

  const rows = providers.data ?? [];
  const live = rows.find((r) => r.isEnabled);

  return (
    <div className="stack">
      <PageHeader
        title="Payments"
        subtitle="Take money online for room bookings and orders from your QR menu"
      />

      <div className="card">
        <div className="card-body">
          <p className="small">
            {live ? (
              <>
                Guests pay through <b>{LABELS[live.provider].name}</b>
                {live.isLive ? '' : ' (test mode)'}. Switch every provider off to collect at the
                hotel instead.
              </>
            ) : (
              <>
                No provider is switched on, so online bookings are <b>held</b> and the guest pays at
                the hotel. That is a perfectly good way to run — turn one on when you want the money
                up front.
              </>
            )}
          </p>
        </div>
      </div>

      {rows.map((row) => {
        const draft = drafts[row.provider] ?? {};
        const label = LABELS[row.provider];
        return (
          <div className="card" key={row.provider}>
            <div className="card-head">
              <h2>{label.name}</h2>
              <div className="row gap-6">
                {row.isEnabled ? <span className="chip ok">On</span> : null}
                {row.isEnabled && !row.isLive ? <span className="chip warn">Test mode</span> : null}
                {row.hasSecret ? <span className="chip">Secret stored</span> : null}
              </div>
            </div>
            <div className="card-body col gap-16">
              <div className="form-grid">
                <TextInput
                  label={label.keyLabel}
                  value={draft.keyId ?? row.keyId}
                  onChange={(e) =>
                    setDrafts({ ...drafts, [row.provider]: { ...draft, keyId: e.target.value } })
                  }
                />
                <TextInput
                  label="Secret"
                  type="password"
                  autoComplete="new-password"
                  hint={
                    row.hasSecret ? 'Leave blank to keep the stored one' : 'Required to switch on'
                  }
                  value={draft.keySecret ?? ''}
                  onChange={(e) =>
                    setDrafts({
                      ...drafts,
                      [row.provider]: { ...draft, keySecret: e.target.value },
                    })
                  }
                />
              </div>

              <div className="row between wrap gap-6">
                <div className="row gap-16 wrap">
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={row.isEnabled}
                      onChange={(e) =>
                        save.mutate({
                          provider: row.provider,
                          ...draft,
                          isEnabled: e.target.checked,
                        })
                      }
                    />
                    <span>Take payments with this</span>
                  </label>
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={row.isLive}
                      onChange={(e) =>
                        save.mutate({ provider: row.provider, ...draft, isLive: e.target.checked })
                      }
                    />
                    <span>Live mode (real money)</span>
                  </label>
                </div>

                <button
                  className="btn primary sm"
                  disabled={save.isPending}
                  onClick={() => save.mutate({ provider: row.provider, ...draft })}
                >
                  Save keys
                </button>
              </div>

              <p className="small muted">{label.note}</p>
            </div>
          </div>
        );
      })}

      <div className="card">
        <div className="card-body">
          <p className="small muted">
            Secrets are encrypted before they are stored and are never sent back to this screen.
            Only Razorpay callbacks are verified end to end today — the others store their keys but
            are not wired to a checkout yet.
          </p>
        </div>
      </div>
    </div>
  );
}
